import React from "react";
import {
  Card,
  Elevation,
  H1,
  H3,
  Button,
  InputGroup,
  HTMLSelect,
  Tag,
  Spinner,
  NonIdealState,
  ControlGroup,
  FormGroup,
  Divider,
  Callout,
  Icon,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import moment from "moment";
import _ from "lodash";

class AuditLogPage extends React.Component {
  state = {
    // Data
    auditLogs: [],
    stats: null,
    actionTypes: [],
    resourceTypes: [],

    // Loading states
    isLoading: false,
    isLoadingStats: false,
    isLoadingActionTypes: false,

    // Filters
    startDate: moment().startOf("day").toDate(),
    endDate: moment().endOf("day").toDate(),
    actionType: "",
    resourceType: "",
    searchText: "",
    actorId: "",
    resourceId: "",

    // Pagination
    currentPage: 1,
    pageSize: 50,
    totalRecords: 0,
    totalPages: 0,

    // Grid
    gridApi: null,
    quickFilterText: "",

    // UI State
    activeView: "logs", // "logs" or "stats"
  };

  componentDidMount() {
    this.loadActionTypes();
    this.loadStats();
    this.loadAuditLogs();
  }

  // Format date for DateInput
  formatDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD/MM/YYYY");
  };

  // Parse date for DateInput
  parseDate = (dateString) => {
    if (!dateString) return null;
    const parsed = moment(dateString, "DD/MM/YYYY");
    return parsed.isValid() ? parsed.toDate() : null;
  };

  // Load action types and resource types
  loadActionTypes = async () => {
    try {
      this.setState({ isLoadingActionTypes: true });
      const response = await apiCallGet(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/actionTypes`);

      if (response.success) {
        this.setState({
          actionTypes: response.data.actionTypes || [],
          resourceTypes: response.data.resourceTypes || [],
        });
      }
    } catch (err) {
      console.error("Error loading action types:", err);
      cogoToast.error("Failed to load filter options");
    } finally {
      this.setState({ isLoadingActionTypes: false });
    }
  };

  // Load audit log statistics
  loadStats = async () => {
    try {
      this.setState({ isLoadingStats: true });
      const response = await apiCallGet(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/stats?days=7`);

      if (response.success) {
        this.setState({ stats: response.data });
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    } finally {
      this.setState({ isLoadingStats: false });
    }
  };

  // Load audit logs with current filters
  loadAuditLogs = async () => {
    try {
      this.setState({ isLoading: true });

      const payload = {
        page: this.state.currentPage,
        limit: this.state.pageSize,
        startDate: moment(this.state.startDate).toISOString(),
        endDate: moment(this.state.endDate).toISOString(),
      };

      // Add optional filters
      if (this.state.actionType) payload.actionType = this.state.actionType;
      if (this.state.resourceType) payload.resourceType = this.state.resourceType;
      if (this.state.searchText) payload.searchText = this.state.searchText;
      if (this.state.actorId) payload.actorId = this.state.actorId;
      if (this.state.resourceId) payload.resourceId = this.state.resourceId;

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/search`, payload);

      if (response.success) {
        this.setState({
          auditLogs: response.data.logs || [],
          totalRecords: response.data.pagination.total || 0,
          totalPages: response.data.pagination.pages || 0,
        });
      } else {
        throw new Error(response.error?.message || "Failed to load audit logs");
      }
    } catch (err) {
      console.error("Error loading audit logs:", err);
      cogoToast.error(err.message || "Failed to load audit logs");
      this.setState({ auditLogs: [] });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  // Handle filter changes
  handleFilterChange = (field, value) => {
    this.setState({ [field]: value, currentPage: 1 }, () => {
      // Debounce search text
      if (field === "searchText") {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.loadAuditLogs();
        }, 500);
      } else {
        this.loadAuditLogs();
      }
    });
  };

  // Handle pagination
  handlePageChange = (newPage) => {
    this.setState({ currentPage: newPage }, this.loadAuditLogs);
  };

  // Clear all filters
  clearFilters = () => {
    this.setState(
      {
        startDate: moment().startOf("day").toDate(),
        endDate: moment().endOf("day").toDate(),
        actionType: "",
        resourceType: "",
        searchText: "",
        actorId: "",
        resourceId: "",
        currentPage: 1,
      },
      this.loadAuditLogs
    );
  };

  // AG Grid column definitions
  columnDefs = [
    {
      headerName: "Date & Time",
      field: "createdAt",
      width: 180,
      cellRenderer: (params) => {
        if (!params.value) return "";
        return moment(params.value).format("DD/MM/YYYY HH:mm:ss");
      },
      pinned: "left",
    },
    {
      headerName: "Action",
      field: "actionType",
      width: 120,
      cellRenderer: (params) => {
        const getActionColor = (action) => {
          switch (action) {
            case "CREATE":
              return "#0F9960";
            case "UPDATE":
              return "#137CBD";
            case "DELETE":
              return "#DB3737";
            case "UPDATE_PRICE":
              return "#D9822B";
            default:
              return "#5C7080";
          }
        };

        return `<span style="color: ${getActionColor(params.value)}; font-weight: 600;">${params.value || ""}</span>`;
      },
    },
    {
      headerName: "Description",
      field: "actionSlug",
      width: 300,
      cellRenderer: (params) => {
        return `<div style="white-space: normal; line-height: 1.3; padding: 4px 0;">${params.value || ""}</div>`;
      },
    },
    {
      headerName: "Resource",
      field: "resourceType",
      width: 150,
      cellRenderer: (params) => {
        return `<span style="color: var(--text-secondary);">${params.value || ""}</span>`;
      },
    },
    {
      headerName: "Resource Name",
      field: "resourceName",
      width: 200,
      cellRenderer: (params) => {
        return `<div style="font-weight: 500;">${params.value || "N/A"}</div>`;
      },
    },
    {
      headerName: "Actor",
      field: "actorName",
      width: 150,
      cellRenderer: (params) => {
        return `<div style="font-weight: 500;">${params.value || "System"}</div>`;
      },
    },
    {
      headerName: "Email",
      field: "actorEmail",
      width: 200,
      cellRenderer: (params) => {
        return `<span style="color: var(--text-secondary); font-size: 12px;">${params.value || ""}</span>`;
      },
    },
  ];

  // Render filters section
  renderFilters = () => {
    return (
      <Card elevation={Elevation.ONE} style={{ padding: "15px", marginBottom: "20px" }}>
        <H3 style={{ color: "var(--text-primary)", marginBottom: "15px" }}>
          <Icon icon="filter" style={{ marginRight: "8px" }} />
          Filters
        </H3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "15px",
          }}
        >
          <FormGroup label="Start Date" labelFor="startDate">
            <DateInput
              value={this.state.startDate}
              onChange={(date) => this.handleFilterChange("startDate", date)}
              format="DD/MM/YYYY"
              formatDate={this.formatDate}
              parseDate={this.parseDate}
              fill
            />
          </FormGroup>

          <FormGroup label="End Date" labelFor="endDate">
            <DateInput
              value={this.state.endDate}
              onChange={(date) => this.handleFilterChange("endDate", date)}
              format="DD/MM/YYYY"
              formatDate={this.formatDate}
              parseDate={this.parseDate}
              fill
            />
          </FormGroup>

          <FormGroup label="Action Type" labelFor="actionType">
            <HTMLSelect
              value={this.state.actionType}
              onChange={(e) => this.handleFilterChange("actionType", e.target.value)}
              fill
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              <option value="">All Actions</option>
              {this.state.actionTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </HTMLSelect>
          </FormGroup>

          <FormGroup label="Resource Type" labelFor="resourceType">
            <HTMLSelect
              value={this.state.resourceType}
              onChange={(e) => this.handleFilterChange("resourceType", e.target.value)}
              fill
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              <option value="">All Resources</option>
              {this.state.resourceTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </HTMLSelect>
          </FormGroup>

          <FormGroup label="Search" labelFor="searchText">
            <InputGroup
              value={this.state.searchText}
              onChange={(e) => this.handleFilterChange("searchText", e.target.value)}
              placeholder="Search descriptions, names..."
              leftIcon="search"
            />
          </FormGroup>

          <div style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
            <Button intent="primary" onClick={this.loadAuditLogs} loading={this.state.isLoading}>
              Apply Filters
            </Button>
            <Button onClick={this.clearFilters}>Clear</Button>
          </div>
        </div>

        {/* Date range warning */}
        {this.state.startDate && this.state.endDate && (
          <Callout intent="warning" style={{ marginTop: "15px" }} icon="info-sign">
            <strong>Date Range:</strong> {moment(this.state.startDate).format("DD/MM/YYYY")} to{" "}
            {moment(this.state.endDate).format("DD/MM/YYYY")}(
            {moment(this.state.endDate).diff(moment(this.state.startDate), "days") + 1} days)
            {moment(this.state.endDate).diff(moment(this.state.startDate), "days") > 30 && (
              <div style={{ marginTop: "5px", color: "#D9822B" }}>⚠️ Large date ranges may take longer to load</div>
            )}
          </Callout>
        )}
      </Card>
    );
  };

  // Render statistics section
  renderStats = () => {
    if (this.state.isLoadingStats) {
      return (
        <Card elevation={Elevation.ONE} style={{ padding: "20px", textAlign: "center" }}>
          <Spinner />
        </Card>
      );
    }

    if (!this.state.stats) return null;

    const { stats } = this.state;

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        {/* Summary Card */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>📊 Summary (Last 7 Days)</H3>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#0F9960" }}>{stats.summary.totalLogs}</div>
              <div style={{ color: "var(--text-secondary)" }}>Total Logs</div>
            </div>
            <div>
              <div style={{ fontSize: "18px", fontWeight: "bold", color: "#137CBD" }}>
                {stats.summary.averagePerDay}
              </div>
              <div style={{ color: "var(--text-secondary)" }}>Per Day</div>
            </div>
          </div>
        </Card>

        {/* Top Actions Card */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>🎯 Top Actions</H3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {stats.breakdown.byActionType.slice(0, 5).map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "14px" }}>{item._id}</span>
                <Tag minimal intent="primary">
                  {item.count}
                </Tag>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Resources Card */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "10px" }}>📋 Top Resources</H3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {stats.breakdown.byResourceType.slice(0, 5).map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "14px" }}>{item._id}</span>
                <Tag minimal intent="success">
                  {item.count}
                </Tag>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Render pagination
  renderPagination = () => {
    if (this.state.totalPages <= 1) return null;

    const { currentPage, totalPages, totalRecords } = this.state;
    const startRecord = (currentPage - 1) * this.state.pageSize + 1;
    const endRecord = Math.min(currentPage * this.state.pageSize, totalRecords);

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 0",
          borderTop: "1px solid var(--border-color)",
          marginTop: "15px",
        }}
      >
        <div style={{ color: "var(--text-secondary)" }}>
          Showing {startRecord}-{endRecord} of {totalRecords} records
        </div>

        <div style={{ display: "flex", gap: "5px" }}>
          <Button
            minimal
            icon="chevron-left"
            disabled={currentPage === 1}
            onClick={() => this.handlePageChange(currentPage - 1)}
          />

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                minimal
                intent={currentPage === pageNum ? "primary" : "none"}
                onClick={() => this.handlePageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}

          <Button
            minimal
            icon="chevron-right"
            disabled={currentPage === totalPages}
            onClick={() => this.handlePageChange(currentPage + 1)}
          />
        </div>
      </div>
    );
  };

  render() {
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>🔍 Audit Logs</H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>Track all system activities and changes</p>
        </div>

        {/* Statistics */}
        {/* {this.renderStats()} */}

        {/* Filters */}
        {this.renderFilters()}

        {/* Main Content */}
        <Card elevation={Elevation.ONE} style={{ padding: "15px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <H3 style={{ margin: 0, color: "var(--text-primary)" }}>Audit Log Entries</H3>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <InputGroup
                leftIcon="search"
                placeholder="Quick filter..."
                value={this.state.quickFilterText}
                onChange={(e) => {
                  this.setState({ quickFilterText: e.target.value });
                  if (this.state.gridApi) {
                    this.state.gridApi.setQuickFilter(e.target.value);
                  }
                }}
                style={{ width: "200px" }}
              />
              <Button icon="refresh" onClick={this.loadAuditLogs} loading={this.state.isLoading} minimal>
                Refresh
              </Button>
            </div>
          </div>

          {this.state.isLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size={40} />
              <div style={{ marginTop: "10px", color: "var(--text-secondary)" }}>Loading audit logs...</div>
            </div>
          ) : this.state.auditLogs.length === 0 ? (
            <NonIdealState
              icon="search"
              title="No Audit Logs Found"
              description="No audit logs match your current filters. Try adjusting the date range or clearing filters."
              action={
                <Button intent="primary" onClick={this.clearFilters}>
                  Clear Filters
                </Button>
              }
            />
          ) : (
            <>
              <div className="ag-theme-alpine-dark" style={{ height: "500px", width: "100%" }}>
                <AgGridReact
                  columnDefs={this.columnDefs}
                  rowData={this.state.auditLogs}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                  }}
                  onGridReady={(params) => {
                    this.setState({ gridApi: params.api });
                    params.api.sizeColumnsToFit();
                  }}
                  suppressRowClickSelection={true}
                  rowHeight={50}
                  headerHeight={40}
                />
              </div>
              {this.renderPagination()}
            </>
          )}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(AuditLogPage));
